import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { LayoutDashboard, CandlestickChart, Plug, FileText, Bell, Banknote, Users, LineChart, Percent, ShieldCheck, CreditCard, ScrollText, Building2, LifeBuoy, Settings, LogOut, Menu, X, Bug, Zap, Activity, AlertTriangle, Wallet, ClipboardCheck, Beaker, Sparkles, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Logo } from "../Logo";
import NotificationBell from "../NotificationBell";
import InvestorBottomNav from "./InvestorBottomNav";

const NAV = {
  investor: [
    { to: "/investor", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/investor/positions", label: "Open Positions", icon: Activity },
    { to: "/investor/trades", label: "Trades", icon: CandlestickChart },
    { to: "/investor/mt5", label: "MT5 Account", icon: Plug },
    { to: "/investor/reports", label: "Reports", icon: FileText },
    { to: "/investor/notifications", label: "Notifications", icon: Bell },
    { to: "/investor/withdraw", label: "Withdrawals", icon: Banknote },
    { to: "/investor/profile", label: "My Profile", icon: Settings },
  ],
  manager: [
    { to: "/manager", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/manager/active", label: "Active Clients", icon: Activity },
    { to: "/manager/inactive", label: "Inactive Clients", icon: AlertTriangle },
    { to: "/manager/all-clients", label: "All Clients", icon: Users },
    { to: "/manager/live-positions", label: "Live Positions", icon: CandlestickChart },
    { to: "/manager/all-trades", label: "All Trades", icon: ScrollText },
    { to: "/manager/intelligence", label: "Trade Intelligence", icon: Sparkles },
    { to: "/manager/signals", label: "Signals", icon: Zap },
    { to: "/manager/clients", label: "Clients", icon: Users },
    { to: "/manager/performance", label: "Performance", icon: LineChart },
  ],
  super_admin: [
    { to: "/admin", label: "Analytics", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Users", icon: ShieldCheck },
    { to: "/admin/mt5-approvals", label: "MT5 Approvals", icon: ClipboardCheck },
    { to: "/admin/payments", label: "Payments", icon: Wallet },
    { to: "/admin/alerts", label: "Alerts", icon: AlertTriangle },
    { to: "/admin/plans", label: "Plans", icon: CreditCard },
    { to: "/admin/brokers", label: "Brokers", icon: Building2 },
    { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText },
    { to: "/admin/tickets", label: "Tickets", icon: LifeBuoy },
    { to: "/admin/executions", label: "Executions", icon: Activity },
    { to: "/admin/debug", label: "MT5 Debug", icon: Bug },
    { to: "/admin/status", label: "STATUS", icon: Beaker },
    { to: "/admin/email", label: "Email Center", icon: Mail },
    { to: "/admin/google-sheets", label: "Google Sheets", icon: FileText },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ],
};

const ROLE_LABEL = { investor: "Investor", manager: "Account Manager", super_admin: "Super Admin" };

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const items = NAV[user?.role] || NAV.investor;
  // Mobile bottom nav is scoped to the Investor area only — never the /manager or /admin panels.
  const isInvestorArea = location.pathname.startsWith("/investor");

  useEffect(() => {
    if (user?.role === "investor")
      api.get("/investor/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, [user]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const sidebar = (
    <div className="flex flex-col h-full">
      <Link to="/" className="px-5 py-5 block">
        <Logo size={50} />
      </Link>
      <div className="px-5 pb-4">
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#7000FF] bg-[#7000FF]/10 border border-[#7000FF]/30 rounded px-2 py-1">{ROLE_LABEL[user?.role]}</span>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
            data-testid={`sidebar-${it.label.toLowerCase().replace(/\s/g, "-")}-link`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#7000FF]/15 text-[#00F0FF] border border-[#7000FF]/30" : "text-gray-400 hover:text-white hover:bg-white/5 border border-transparent"}`}>
            <it.icon size={17} strokeWidth={1.5} /> {it.label}
            {it.label === "Notifications" && unread > 0 && <span className="ml-auto text-[10px] bg-[#FF3B30] text-white rounded-full px-1.5 py-0.5">{unread}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <p className="text-sm text-white truncate">{user?.name}</p>
        <p className="text-xs text-gray-500 truncate mb-3">{user?.email}</p>
        <button data-testid="logout-button" onClick={doLogout} className="btn-outline w-full py-2 text-xs flex items-center justify-center gap-2">
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-gray-200 flex">
      <aside className="hidden lg:block w-60 shrink-0 border-r border-white/10 bg-[#0a0a0a] fixed inset-y-0">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0a0a0a] border-r border-white/10">{sidebar}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-40 glass flex items-center justify-between px-4 sm:px-6 h-14 border-b border-white/5">
          <button data-testid="mobile-sidebar-button" onClick={() => setOpen(true)} className="text-gray-300 lg:hidden"><Menu size={20} /></button>
          <div className="lg:hidden"><Logo size={36} /></div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <NotificationBell />
          </div>
        </header>
        <main className={`p-4 sm:p-6 lg:p-8 max-w-[1500px] ${isInvestorArea ? "pb-28 lg:pb-8" : ""}`}><Outlet /></main>
      </div>
      {isInvestorArea && <InvestorBottomNav onMore={() => setOpen(true)} />}
    </div>
  );
}
